import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { getCategoriesPh } from '../..';
import { GetCategoriesPhResponse } from '../../interfaces';
import { UseGetCategoriesPhRequest } from './interfaces';

/**
 * Fetches the pharmacy category tree using React Query.
 *
 * This query is highly cached:
 * - Does not refetch on mount or window focus
 * - Data is always considered fresh (`staleTime: Infinity`)
 * - No automatic retries
 *
 * The query can be conditionally enabled via the `enabled` flag.
 *
 * @param posId - Point of Sale identifier
 * @param enabled - Controls whether the query should execute
 *
 * @returns React Query result containing the category tree structure
 *
 * @example
 * ```tsx
 * const { data, isLoading } = useGetCategoriesPh({
 *   posId: "pos-123",
 *   enabled: true,
 * });
 *
 * if (isLoading) return <span>Loading...</span>;
 *
 * return (
 *   <ul>
 *     {data?.map(category => (
 *       <li key={category.id}>{category.name}</li>
 *     ))}
 *   </ul>
 * );
 * ```
 */
export function useGetCategoriesPh({
	enabled,
	posId,
}: UseGetCategoriesPhRequest): UseQueryResult<GetCategoriesPhResponse['content'], unknown> {
	return useQuery({
		queryKey: ['useGetCategoriesPh', posId],
		queryFn: () => getCategoriesPh({ posId }),
		enabled,
	});
}
